import type { InspectionListParams } from '@/api/endpoints/inspections';
import type { AdminUserListParams, AuditLogParams } from '@/api/endpoints/admin';
import type { ExportHistoryParams } from '@/api/endpoints/exports';
import type { InspectionFilters, ReferenceResource } from '@/types';

/**
 * Centralized TanStack Query key factory. Keeping this in one module avoids
 * key-shape drift between the hook that fetches data and the mutation that
 * invalidates it.
 */
export const queryKeys = {
  me: ['me'] as const,
  inspections: {
    list: (params: InspectionListParams) => ['inspections', 'list', params] as const,
    detail: (id: string) => ['inspections', 'detail', id] as const,
  },
  analytics: {
    summary: (filters: InspectionFilters) => ['analytics', 'summary', filters] as const,
    trend: (filters: InspectionFilters, granularity: 'day' | 'week') =>
      ['analytics', 'trend', filters, granularity] as const,
    topDefects: (filters: InspectionFilters, limit: number) =>
      ['analytics', 'top-defects', filters, limit] as const,
    byProcess: (filters: InspectionFilters) => ['analytics', 'by-process', filters] as const,
  },
  reference: {
    list: (resource: ReferenceResource) => ['reference', resource] as const,
  },
  exports: {
    history: (params: ExportHistoryParams) => ['exports', 'history', params] as const,
    status: (jobId: string) => ['exports', 'status', jobId] as const,
  },
  admin: {
    users: (params: AdminUserListParams) => ['admin', 'users', params] as const,
    auditLog: (params: AuditLogParams) => ['admin', 'audit-log', params] as const,
    backups: (params: AdminUserListParams) => ['admin', 'backups', 'history', params] as const,
    backup: (id: string | null) => ['admin', 'backups', 'status', id] as const,
  },
};
